import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { api, loadUser } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Briefcase, Users, Calendar, IndianRupee, ArrowRight, CheckCircle2, XCircle, Bookmark, BookmarkCheck, Sparkles } from "lucide-react";

export default function JobDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [match, setMatch] = useState(null);
  const [saved, setSaved] = useState(false);
  const user = loadUser();

  useEffect(()=>{
    api.get(`/jobs/${id}`).then(r=>setJob(r.data)).catch(()=>{});
    api.post(`/jobs/${id}/view`).catch(()=>{});
    if (user?.role === "candidate") {
      api.get(`/candidate/skill-gap/${id}`).then(r=>setMatch(r.data)).catch(()=>{});
      api.get("/saved-jobs").then(r => setSaved(r.data.some(j => j.id === id))).catch(()=>{});
    }
  },[id, user?.role]);

  const toggleSave = async () => {
    try {
      if (saved) { await api.delete(`/saved-jobs/${id}`); setSaved(false); toast.success("Removed from saved"); }
      else { await api.post(`/saved-jobs/${id}`); setSaved(true); toast.success("Saved to your list"); }
    } catch(e){ toast.error("Please sign in to save jobs"); }
  };

  if (!job) return <div className="min-h-screen"><PublicHeader/><div className="max-w-7xl mx-auto p-10 text-slate-500">Loading...</div></div>;
  const onApply = () => { if (!user) nav(`/login?next=/apply/${id}`); else nav(`/apply/${id}`); };

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />
      <section className="bg-[#0A2540] text-white py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="bg-white/10 text-white border-white/20 hover:bg-white/10">{job.department}</Badge>
            {job.domain && <Badge className="bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/30 hover:bg-[#F59E0B]/20">{job.domain}</Badge>}
            {job.experience_category && <Badge className="bg-white/5 text-slate-200 border-white/10 hover:bg-white/5">{job.experience_category}</Badge>}
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold mt-4">{job.title}</h1>
          <div className="mt-4 flex flex-wrap gap-6 text-slate-300 text-sm">
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4"/>{job.location}</span>
            <span className="flex items-center gap-2"><Briefcase className="w-4 h-4"/>{job.experience_min}–{job.experience_max} yrs · {job.employment_type}</span>
            <span className="flex items-center gap-2"><Users className="w-4 h-4"/>{job.openings} openings</span>
            {job.salary_range && <span className="flex items-center gap-2"><IndianRupee className="w-4 h-4"/>{job.salary_range}</span>}
          </div>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-8">
          {match && (
            <Card className="p-6 border-slate-200 bg-gradient-to-br from-blue-50 to-white" data-testid="match-card">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#2563EB] font-semibold"><Sparkles className="w-3 h-3"/>Your Match</div>
                  <div className="font-display text-5xl font-bold text-[#0A2540] mt-1">{match.match}%</div>
                </div>
                <div className="grid grid-cols-2 gap-4 flex-1 min-w-64 max-w-md">
                  {Object.entries(match.breakdown).map(([k,v])=>(
                    <div key={k}>
                      <div className="flex justify-between text-xs"><span className="capitalize text-slate-600">{k}</span><span className="font-semibold text-[#0A2540]">{v}%</span></div>
                      <Progress value={v} className="h-1.5 mt-1"/>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-5 grid md:grid-cols-2 gap-5">
                <div>
                  <div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold mb-2">Matching skills</div>
                  <div className="flex flex-wrap gap-1.5">
                    {match.matching_skills.length ? match.matching_skills.map(s=>(
                      <Badge key={s} className="bg-emerald-100 text-emerald-800 border-none hover:bg-emerald-100"><CheckCircle2 className="w-3 h-3 mr-1"/>{s}</Badge>
                    )) : <span className="text-xs text-slate-500">Add skills to your profile to see matches</span>}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-amber-700 font-semibold mb-2">Potential gaps</div>
                  <div className="flex flex-wrap gap-1.5">
                    {(match.missing_required.length + match.missing_preferred.length) === 0 ? <span className="text-xs text-emerald-600">You match every skill for this role.</span> :
                      <>
                        {match.missing_required.map(s=>(
                          <Badge key={s} className="bg-red-100 text-red-800 border-none hover:bg-red-100"><XCircle className="w-3 h-3 mr-1"/>{s} · Required</Badge>
                        ))}
                        {match.missing_preferred.map(s=>(
                          <Badge key={s} className="bg-slate-100 text-slate-700 border-none hover:bg-slate-100">{s} · Preferred</Badge>
                        ))}
                      </>
                    }
                  </div>
                </div>
              </div>
            </Card>
          )}
          <div>
            <h2 className="font-display text-xl font-semibold text-[#0A2540]">About the Role</h2>
            <p className="mt-3 text-slate-700 leading-relaxed">{job.about}</p>
          </div>
          <Separator/>
          <div>
            <h2 className="font-display text-xl font-semibold text-[#0A2540]">Responsibilities</h2>
            <ul className="mt-3 space-y-2">
              {job.responsibilities?.map((r,i)=><li key={i} className="flex gap-2 text-slate-700"><CheckCircle2 className="w-4 h-4 text-[#2563EB] mt-1 shrink-0"/>{r}</li>)}
            </ul>
          </div>
          <div>
            <h2 className="font-display text-xl font-semibold text-[#0A2540]">Required Qualifications</h2>
            <ul className="mt-3 space-y-2">
              {job.required_qualifications?.map((r,i)=><li key={i} className="flex gap-2 text-slate-700"><CheckCircle2 className="w-4 h-4 text-[#2563EB] mt-1 shrink-0"/>{r}</li>)}
            </ul>
          </div>
          {job.preferred_qualifications?.length>0 && <div>
            <h2 className="font-display text-xl font-semibold text-[#0A2540]">Preferred Qualifications</h2>
            <ul className="mt-3 space-y-2">
              {job.preferred_qualifications.map((r,i)=><li key={i} className="flex gap-2 text-slate-700"><CheckCircle2 className="w-4 h-4 text-slate-400 mt-1 shrink-0"/>{r}</li>)}
            </ul>
          </div>}
          <div>
            <h2 className="font-display text-xl font-semibold text-[#0A2540]">Skills</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {job.required_skills?.map(s=><Badge key={s} className="bg-blue-100 text-blue-800 border-none hover:bg-blue-100">{s}</Badge>)}
              {job.preferred_skills?.map(s=><Badge key={s} variant="outline">{s}</Badge>)}
            </div>
          </div>
          {job.benefits?.length>0 && <div>
            <h2 className="font-display text-xl font-semibold text-[#0A2540]">What We Offer</h2>
            <ul className="mt-3 grid md:grid-cols-2 gap-2">
              {job.benefits.map((r,i)=><li key={i} className="flex gap-2 text-slate-700"><CheckCircle2 className="w-4 h-4 text-[#F59E0B] mt-1 shrink-0"/>{r}</li>)}
            </ul>
          </div>}
        </div>
        <aside>
          <Card className="p-6 sticky top-24 border-slate-200">
            <div className="text-sm text-slate-500">Job ID</div>
            <div className="font-mono text-xs text-[#0A2540] mt-1">{job.id}</div>
            <Button onClick={onApply} className="w-full mt-6 bg-[#0A2540] hover:bg-[#0A2540]/90" data-testid="job-apply-button">
              Apply Now <ArrowRight className="w-4 h-4 ml-2"/>
            </Button>
            {user?.role === "candidate" && (
              <Button onClick={toggleSave} variant="outline" className="w-full mt-3" data-testid="job-save-button">
                {saved ? <><BookmarkCheck className="w-4 h-4 mr-2 text-[#F59E0B]"/>Saved</> : <><Bookmark className="w-4 h-4 mr-2"/>Save Job</>}
              </Button>
            )}
            <p className="text-xs text-slate-500 mt-4">A skill-based assessment will be automatically assigned when you apply.</p>
            {job.deadline && <div className="mt-6 text-sm flex items-center gap-2 text-slate-600"><Calendar className="w-4 h-4"/>Deadline: {job.deadline}</div>}
            <Separator className="my-6"/>
            <div className="text-sm text-slate-600 leading-relaxed">
              <div className="font-semibold text-[#0A2540] mb-1">About VS-Solutions</div>
              Vijay Software Solutions Pvt. Ltd. builds enterprise-grade software for global clients.
            </div>
          </Card>
        </aside>
      </section>
      <PublicFooter />
    </div>
  );
}
